// src/utils/inputValidation.js
/**
 * Input validation and sanitization utilities
 */

/**
 * Sanitize string input - ลบ HTML tags และ special characters ที่อันตราย
 * @param {string} input - Input string to sanitize
 * @returns {string} Sanitized string
 */
export function sanitizeString(input) {
  if (typeof input !== "string") return "";
  
  // ลบ HTML tags
  let sanitized = input.replace(/<[^>]*>/g, "");
  
  // ลบ script tags และ event handlers
  sanitized = sanitized.replace(/javascript:/gi, "");
  sanitized = sanitized.replace(/on\w+\s*=/gi, "");
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  return sanitized;
}

/**
 * Validate email format
 * @param {string} email - Email to validate
 * @returns {boolean} true if valid email format
 */
export function isValidEmail(email) {
  if (!email || typeof email !== "string") return false;
  
  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  
  return emailRegex.test(email.trim()) && email.length <= 254;
}

/**
 * Validate phone number (flexible format)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} true if valid phone format
 */
export function isValidPhone(phone) {
  if (!phone || typeof phone !== "string") return false;
  
  // Allow digits, +, -, spaces, parentheses
  const phoneRegex = /^[\d\s+\-().]{6,20}$/;
  
  return phoneRegex.test(phone.trim());
}

/**
 * Sanitize and validate email
 * @param {string} email - Email to sanitize and validate
 * @returns {object} { isValid: boolean, value: string, error: string }
 */
export function sanitizeAndValidateEmail(email) {
  const sanitized = sanitizeString(email);
  
  if (!sanitized) {
    return { isValid: false, value: "", error: "Email is required" };
  }
  
  if (!isValidEmail(sanitized)) {
    return { isValid: false, value: sanitized, error: "Invalid email format" };
  }
  
  return { isValid: true, value: sanitized.toLowerCase(), error: "" };
}

/**
 * Sanitize text input with length limit
 * @param {string} input - Input to sanitize
 * @param {number} maxLength - Maximum length (default: 1000)
 * @returns {string} Sanitized string
 */
export function sanitizeText(input, maxLength = 1000) {
  const sanitized = sanitizeString(input);
  
  if (sanitized.length > maxLength) {
    return sanitized.substring(0, maxLength);
  }
  
  return sanitized;
}

/**
 * Validate required field
 * @param {string} value - Value to validate
 * @param {string} fieldName - Name of the field (for error message)
 * @returns {object} { isValid: boolean, error: string }
 */
export function validateRequired(value, fieldName = "Field") {
  const sanitized = sanitizeString(value);
  
  if (!sanitized || sanitized.length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }
  
  return { isValid: true, error: "" };
}

/**
 * Sanitize form data object
 * @param {object} formData - Form data object
 * @returns {object} Sanitized form data
 */
export function sanitizeFormData(formData) {
  const sanitized = {};
  
  for (const [key, value] of Object.entries(formData)) {
    if (typeof value === "string") {
      sanitized[key] = sanitizeString(value);
    } else if (Array.isArray(value)) {
      sanitized[key] = value.map(item => 
        typeof item === "string" ? sanitizeString(item) : item
      );
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeFormData(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}
